/**
 * Chuẩn hoá và so khớp CIDR, IPv4 và IPv6 (ADR-0059 mục 6.2).
 *
 * Bản giao diện của `CidrNormalizer` ở `libs/platform-common`: cùng luật
 * (`10.0.0.5/24` → `10.0.0.0/24`, IP trần thành `/32` hoặc `/128`, IPv6 viết
 * dạng rút gọn RFC 5952) để ô nhập báo trước **đúng** chuỗi backend sẽ lưu.
 * Backend vẫn là chốt chặn; ở đây chỉ để người dùng thấy trước.
 *
 * Hàm thuần: không dựng chuỗi hiển thị, trả mã lỗi để view tự dịch.
 */

export type KetQuaCidr =
  | { ok: true; cidr: string; phienBan: 4 | 6; tienTo: number; daDoi: boolean }
  | { ok: false; loi: 'dinh_dang' | 'tien_to' };

const BON_OCTET = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/** `a.b.c.d` → số 32 bit không dấu; sai thì `null`. Từ chối số 0 đứng đầu (`010`). */
export function docIpv4(s: string): number | null {
  const m = BON_OCTET.exec(s);
  if (!m) return null;
  let kq = 0;
  for (let i = 1; i <= 4; i++) {
    const phan = m[i]!;
    if (phan.length > 1 && phan.startsWith('0')) return null;
    const n = Number(phan);
    if (n > 255) return null;
    kq = kq * 256 + n;
  }
  return kq;
}

export function vietIpv4(n: number): string {
  return [24, 16, 8, 0].map((dich) => Math.floor(n / 2 ** dich) % 256).join('.');
}

/** IPv6 (kể cả `::` và đuôi IPv4 nhúng) → `bigint` 128 bit; sai thì `null`. */
export function docIpv6(dauVao: string): bigint | null {
  let s = dauVao;
  if (!/^[0-9a-fA-F:.]+$/.test(s) || !s.includes(':')) return null;

  if (s.includes('.')) {
    const cuoi = s.lastIndexOf(':');
    const v4 = docIpv4(s.slice(cuoi + 1));
    if (v4 === null) return null;
    s = `${s.slice(0, cuoi + 1)}${Math.floor(v4 / 65536).toString(16)}:${(v4 % 65536).toString(16)}`;
  }

  const hai = s.split('::');
  if (hai.length > 2) return null;
  const dau = hai[0] ? hai[0].split(':') : [];
  const duoi = hai.length === 2 && hai[1] ? hai[1].split(':') : [];
  if (hai.length === 1 && dau.length !== 8) return null;
  if (hai.length === 2 && dau.length + duoi.length > 7) return null;

  const lap = hai.length === 2 ? 8 - dau.length - duoi.length : 0;
  const nhom = [...dau, ...Array<string>(lap).fill('0'), ...duoi];
  let kq = 0n;
  for (const g of nhom) {
    if (!/^[0-9a-fA-F]{1,4}$/.test(g)) return null;
    kq = (kq << 16n) | BigInt(parseInt(g, 16));
  }
  return kq;
}

/** Viết IPv6 theo RFC 5952: chữ thường, bỏ số 0 đầu, `::` cho dãy 0 dài nhất (≥ 2 nhóm). */
export function vietIpv6(n: bigint): string {
  const nhom: number[] = [];
  for (let i = 7; i >= 0; i--) nhom.push(Number((n >> BigInt(i * 16)) & 0xffffn));

  let batDau = -1;
  let dai = 0;
  for (let i = 0; i < 8; ) {
    if (nhom[i] !== 0) {
      i++;
      continue;
    }
    let j = i;
    while (j < 8 && nhom[j] === 0) j++;
    if (j - i > dai) {
      batDau = i;
      dai = j - i;
    }
    i = j;
  }

  const hex = nhom.map((g) => g.toString(16));
  if (dai < 2) return hex.join(':');
  return `${hex.slice(0, batDau).join(':')}::${hex.slice(batDau + dai).join(':')}`;
}

const TAT_CA_V6 = (1n << 128n) - 1n;

export function matNa6(tienTo: number): bigint {
  return tienTo === 0 ? 0n : TAT_CA_V6 ^ ((1n << BigInt(128 - tienTo)) - 1n);
}

/** Phần mạng của một IPv4 (dùng phép số học, tránh dấu của toán tử bit). */
export function mang4(ip: number, tienTo: number): number {
  const khoi = 2 ** (32 - tienTo);
  return Math.floor(ip / khoi) * khoi;
}

/** Tách `ip/tiền tố`; IP trần lấy tiền tố đầy đủ. */
function tach(s: string): { ip: string; tienTo: string | null } {
  const i = s.indexOf('/');
  return i < 0 ? { ip: s, tienTo: null } : { ip: s.slice(0, i), tienTo: s.slice(i + 1) };
}

function docTienTo(s: string | null, toiDa: number): number | null {
  if (s === null) return toiDa;
  if (!/^\d{1,3}$/.test(s)) return null;
  const n = Number(s);
  return n <= toiDa ? n : null;
}

/** Chuẩn hoá một CIDR hoặc IP trần về dạng backend sẽ lưu. */
export function chuanHoaCidr(dauVao: string): KetQuaCidr {
  const s = dauVao.trim();
  const { ip, tienTo } = tach(s);

  const v4 = docIpv4(ip);
  if (v4 !== null) {
    const p = docTienTo(tienTo, 32);
    if (p === null) return { ok: false, loi: 'tien_to' };
    const cidr = `${vietIpv4(mang4(v4, p))}/${p}`;
    return { ok: true, cidr, phienBan: 4, tienTo: p, daDoi: cidr !== s };
  }

  const v6 = docIpv6(ip);
  if (v6 !== null) {
    const p = docTienTo(tienTo, 128);
    if (p === null) return { ok: false, loi: 'tien_to' };
    const cidr = `${vietIpv6(v6 & matNa6(p))}/${p}`;
    return { ok: true, cidr, phienBan: 6, tienTo: p, daDoi: cidr !== s };
  }

  return { ok: false, loi: 'dinh_dang' };
}

/** `ip` có nằm trong `cidr` không. Khác phiên bản hoặc chuỗi hỏng → `false`. */
export function ipThuocCidr(ip: string, cidr: string): boolean {
  const mang = chuanHoaCidr(cidr);
  const dc = chuanHoaCidr(ip);
  if (!mang.ok || !dc.ok || mang.phienBan !== dc.phienBan) return false;

  if (mang.phienBan === 4) {
    const a = docIpv4(tach(dc.cidr).ip)!;
    const b = docIpv4(tach(mang.cidr).ip)!;
    return mang4(a, mang.tienTo) === b;
  }
  const a = docIpv6(tach(dc.cidr).ip)!;
  const b = docIpv6(tach(mang.cidr).ip)!;
  return (a & matNa6(mang.tienTo)) === b;
}

/** CIDR phủ mọi địa chỉ (`0.0.0.0/0`, `::/0`) — cấm trong blocklist. */
export function laTatCaDiaChi(cidr: string): boolean {
  const kq = chuanHoaCidr(cidr);
  return kq.ok && kq.tienTo === 0;
}
